import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import { green } from "@material-ui/core/colors";
import { makeStyles } from "@material-ui/core/styles";
import { Avatar, Badge, Divider, ListItemAvatar, ListItem, ListItemText, Tooltip, Typography } from "@material-ui/core";
import AccessTimeOutlinedIcon from "@material-ui/icons/AccessTimeOutlined";
import ChatBubbleOutlineOutlinedIcon from "@material-ui/icons/ChatBubbleOutlineOutlined";
import CloseOutlinedIcon from "@material-ui/icons/CloseOutlined";
import PersonOutlinedIcon from "@material-ui/icons/PersonOutlined";
import PhoneAndroidOutlinedIcon from "@material-ui/icons/PhoneAndroidOutlined";
import ReplyOutlinedIcon from "@material-ui/icons/ReplyOutlined";
import RestoreOutlinedIcon from "@material-ui/icons/RestoreOutlined";
import SimCardOutlinedIcon from "@material-ui/icons/SimCardOutlined";
import SyncAltOutlinedIcon from "@material-ui/icons/SyncAltOutlined";

import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import adjustTicketTimeNumber from "../../utils/adjustTicketTimeNumber";
import BezinhoImg from "../../assets/miscSvg/bezinho.svg";
import botIcon from "../../assets/ticketsTypes/bot.svg";
import chatIcon from "../../assets/ticketsTypes/chat.png";
import ContactTagsModal from "../ContactTagsModal";
import EditTicketModal from "../EditTicketModal";
import internalChatIcon from "../../assets/ticketsTypes/internalChatIcon.png";
import InternalChatImg from "../../assets/miscPng/internalChat.png";
import InternalGroupImg from "../../assets/miscPng/internalGroup.png";
import MarkdownWrapper from "../MarkdownWrapper";
import millisecondsToTime from "../../utils/datetime/millisecondsToTime";
import TicketOptionsContextMenu from "../TicketOptionsContextMenu";
import TicketPropertiesModal from "../TicketPropertiesModal";
import TransferTicketModal from "../TransferTicketModal";
import truncateString from "../../utils/truncateString";
import whatsappIcon from "../../assets/ticketsTypes/whatsapp.png";
import WhatsappOfficialImg from "../../assets/miscJpg/whatsappOfficial.jpg";

import DefaultFunctionsUtils from "../../utils/classes/DefaultFunctionsUtils";
import DefaultValuesUtils from "../../utils/classes/DefaultValuesUtils";

import { useGlobalRegisters } from "../../context/GlobalRegisters/GlobalRegistersContext";

const useStyles = makeStyles(theme => ({
	simpleIcon: { fontSize: "20px", },

	horizontalFlip: { rotate: "y 180deg", fontSize: "20px", },

	svgIcons: { borderRadius:"20px", fontSize: "15px", },

	ticket: {
		borderRadius:"20px",
		position: "relative",
		"&.Mui-selected": { backgroundColor: theme.palette.background.selectedTicket },
		"&.Mui-selected:hover": { backgroundColor: theme.palette.background.selectedTicket },
	},

	contactNameWrapper: { borderRadius: "20px", display: "flex", justifyContent: "space-between", },

	contactBadgeWrapper: {
		borderRadius: "20px",
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		gap: "1.65em"
	},

	newBadge: { borderRadius: "20px", alignSelf: "center", marginRight: 8, marginLeft: "auto", },

	contactNameAndDevice: { display: "flex", flexDirection: "row", alignItems: "center", gap: "5px", },
	ticketType: { width: "20px", },
	lastMessageTime: { borderRadius:"20px", justifySelf: "flex-end", },

	badgeList: {
		borderRadius:"20px",
		adding: "3px",
		color: "#f6f6ff",
		fontSize: "10px",
		
		display: "flex",
		gap: "3px",
		alignItems: "center",
	},

	customBadge: {
		border: `1px solid ${theme.palette.primary.main}`,
		color: theme.palette.text.primary,

		padding: "3px",
		paddingLeft: "10px",
		paddingRight: "10px",
		borderRadius: "15px",
		fontSize: "10px",
		width: "fit-content",
		display: "flex",
		gap: "3px",
		alignItems: "center",
	},

	customBadgeAwaitingTime: {
		border: `1px solid ${theme.palette.primary.main}`,
		color: theme.palette.text.primary,

		padding: "3px",
		paddingLeft: "10px",
		paddingRight: "10px",
		borderRadius: "15px",
		fontSize: "10px",
		width: "fit-content",
		display: "flex",
		gap: "3px",
		alignItems: "center",
	},

	customLabelBadge: {
		transition: "all 0.3s ease",
		marginBottom: "2px",
		"&:hover": { cursor: "pointer", transform: "translateY(-5px)", opacity: 0.8, },

		color: theme.palette.text.primary,

		padding: "3px",
		paddingLeft: "10px",
		paddingRight: "10px",
		borderRadius: "15px",
		fontSize: "10px",
		width: "fit-content",
		display: "flex",
		gap: "3px",
		alignItems: "center",		
	},

	customBadgeAwaitingTimeAlert: {
		backgroundColor: theme.palette.primary.main,
		color: "#f5f5ff",

		padding: "3px",
		paddingLeft: "10px",
		paddingRight: "10px",
		borderRadius: "15px",
		fontSize: "10px",
		width: "fit-content",
		display: "flex",
		gap: "3px",
		alignItems: "center",
	},

	chipBadge: {
		border: `1px solid ${theme.palette.primary.main}`,
		padding: "3px",
		paddingLeft: "10px",
		paddingRight: "10px",
		borderRadius: "15px",
		color: theme.palette.text.primary,
		fontSize: "10px",
		width: "fit-content",
		marginRight: 0,
		marginLeft: "auto",

		display: "flex",
		gap: "3px",
		alignItems: "center",
		justifySelf: "flex-end",
	},

	contactLastMessage: { paddingRight: 20, width: "100%" },
	badgeStyle: { color: "white", backgroundColor: green[500], },
}));

const TicketListItem = ({ ticket, ticketsList, setTabOpen, resetAudioPlayer }) => {
	// 	***************
	// 	** Variables **
	// 	***************
	const classes = useStyles();
	const history = useHistory();
	const isMounted = useRef(true);
	const { ticketId } = useParams();
	const { user } = useContext(AuthContext);

	const [anchorEl, setAnchorEl] = useState(null);
	const ticketOptionsContextMenuOpen = Boolean(anchorEl);

	const [attendanceTimeSetting, setAttendanceTimeSetting] = useState(30);
	const [awaitingTimeSetting, setAwaitingTimeSetting] = useState(30);
	const [contactTagsModalOpen, setContactTagsModalOpen] = useState(false);
	const [prioritizeUserSetting, setPrioritizeUserSetting] = useState("disabled");
	const [prioritizeUserTimeSetting, setPrioritizeUserTime] = useState(2);

	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	const [editTicketModalOpen, setEditTicketModalOpen] = useState(false);
	const [propertiesModalOpen, setPropertiesModalOpen] = useState(false);

	const {
		getSetting
		, isLoading
		, setContactViewContactId
		, setContactViewWhatsappId
		, setShouldSyncRouteOnMenu
	} = useGlobalRegisters();
	


	// 	*****************
	// 	** Use Effects **
	// 	*****************
	useEffect(() => {
		return () => isMounted.current = false;
	}, []);

	useEffect(() => {
		if (isLoading) return;
		
		const AttendanceTimeResponse = getSetting("attendanceTime");
		const AwaitingTimeResponse = getSetting("awaitingTime");
		const PrioritizeUserResponse = getSetting("prioritizeUser");
		const PrioritizeUserTimeResponse = getSetting("prioritizeUserTime");

		if (AttendanceTimeResponse) setAttendanceTimeSetting(parseInt(AttendanceTimeResponse, 10));
		if (AwaitingTimeResponse) setAwaitingTimeSetting(parseInt(AwaitingTimeResponse, 10));
		if (PrioritizeUserResponse) setPrioritizeUserSetting(PrioritizeUserResponse);
		if (PrioritizeUserTimeResponse) {
			if (PrioritizeUserTimeResponse === "infinity") setPrioritizeUserTime(PrioritizeUserTimeResponse);
			else setPrioritizeUserTime(parseInt(PrioritizeUserTimeResponse, 10));
		}
	}, [getSetting, isLoading]);



	// 	***************
	// 	** Functions **
	// 	***************
	const handleOpenContactTagsModal = () => {
		setContactTagsModalOpen(true);
	};

	const handleCloseContactTagsModal = () => {
		setContactTagsModalOpen(false);
	};

	const handleOpenEditTicketModal = () => {
		setEditTicketModalOpen(true);
	};
	
	const handleCloseEditTicketModal = () => {
		if (isMounted.current) setEditTicketModalOpen(false);
		setEditTicketModalOpen(false);
	};
	
	const handleOpenTransferModal = () => {
		setTransferTicketModalOpen(true);
	};
	
	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) setTransferTicketModalOpen(false);
	};

	const handleOpenTicketOptionsMenu = (event, ticket) => {
		if (!ticket) return;
		setAnchorEl(event.currentTarget);
	};

	const handleCloseTicketOptionsMenu = () => {
		setAnchorEl(null);
	}

	const handleContextMenu = (event, ticket) => {
		event.preventDefault();
		if (ticket && ticket.status !== undefined) handleOpenTicketOptionsMenu(event, ticket);
	};

	const formatSelectedTicketContactIdForLocalStorage = (ticketId, ticketType, contactId) => {
		const ticketTypeNumber = parseInt(ticketType, 10);

		if (ticketTypeNumber === 0) return contactId;
		else if (ticketTypeNumber === 1) return `0-note-${ticketId}`;
		else if (ticketTypeNumber === 2) return `0-chatBot-${ticketId}`;
		else if (ticketTypeNumber === 3) return `0-internalChat-${ticketId}`;
		else if (ticketTypeNumber === 4) return `0-internalGroup-${ticketId}`;
	};

	const handleSelectTicket = (id, type, status, contactId) => {
		localStorage.setItem("selectedTicketContactId", formatSelectedTicketContactIdForLocalStorage(id, type, contactId));
		localStorage.setItem("tabOpenTickets", status);

		const shouldResetAudioPlayer = localStorage.getItem("selectedTicketContactId") === localStorage.getItem("currentPlayingAudioContactId");
		if (shouldResetAudioPlayer) resetAudioPlayer();
		
		if (localStorage.getItem("selectedTicketContactId") === localStorage.getItem("currentPlayingAudioContactId")) resetAudioPlayer();

		const ticketStatusPageParam = ["group", "open", "pending"].includes(status) ? "open" : "closed";

		if (type === 0) {
			setContactViewContactId(contactId);
			setContactViewWhatsappId(ticket.whatsappId);
		} else {
			setContactViewContactId(0);
			setContactViewWhatsappId(0);
		}

		DefaultFunctionsUtils.redirectToTicketsPage(history, `/${id}?tab=${ticketStatusPageParam}`, ticketStatusPageParam === "open" ? 0 : 1);
		setShouldSyncRouteOnMenu(true);
	};

	const timeAwaiting = millisecondsToTime(new Date() - new Date(ticket.createdAt));
	const secondMetrics = [i18n.t("ticketsList.timeAwaitSecond"), i18n.t("ticketsList.timeAwaitSecond") + "s"];
	
	let [timeAwaitingNumber, timeAwaitingMetric] = [parseInt(timeAwaiting.split(" ")[0]), timeAwaiting.split(" ")[1]];
	timeAwaitingNumber = adjustTicketTimeNumber(timeAwaitingNumber, timeAwaitingMetric);

	let timeInAttendance;

	// se ticket já foi fechado, tempo de atendimento será de quando um usuário
	// o pegou até quando o ticket foi atualizado pela última vez
	if (ticket.userId !== null && ticket.status === "closed") {
		timeInAttendance = millisecondsToTime(new Date(ticket.isClosedAt) - new Date(ticket.withUserAt));
	}

	// se ticket consta em aberto, tempo de atendimento será de quando um usuário
	// o pegou até o momento atual em que está se vendo o tempo
	else if (ticket.userId !== null && ticket.status === "open") {
		timeInAttendance = millisecondsToTime(new Date() - new Date(ticket.withUserAt));
	}

	// caso o ticket ainda não tenha um usuário vinculado, o tempo de atendimento
	// será zero
	else {
		timeInAttendance = 0;
	}

	let timeAttendanceNumber = timeInAttendance === 0 ? 0 : parseInt(timeInAttendance.split(" ")[0]);
	let timeAttendanceMetric = timeInAttendance === 0 ? 0 : timeInAttendance.split(" ")[1];

	if (timeInAttendance !== 0) {
		timeAttendanceNumber = adjustTicketTimeNumber(timeAttendanceNumber, timeAttendanceMetric);
	}

	const { whatsApps } = useContext(WhatsAppsContext);
	const showChipBadge = whatsApps?.length > 1;

	let ticketTypeIcon;
	if (ticket.type === 0) ticketTypeIcon = whatsappIcon;
	else if (ticket.type === 1) ticketTypeIcon = chatIcon;
	else if (ticket.type === 2) ticketTypeIcon = botIcon;
	else ticketTypeIcon = internalChatIcon;

	let userExists;

	try {
		userExists = ticket.type === 0 ? ticket?.contact?.users.some(contactUser => contactUser.id === user.id) : [];
	} catch (error) {
		userExists = [];
	}

	const showTicketFlag = (
		prioritizeUserSetting === "disabled"
		|| (
			ticket?.status !== "pending"
			|| ticket?.type === 1 || ticket?.type === 2
			|| (ticket?.type === 0 && (
				ticket?.contact && ticket?.contact.users &&
				(
					(ticket?.contact.users.length === 0)
					|| (ticket?.contact.users.length > 0 && userExists)
					|| (
							ticket?.contact.users.length > 0
							&& prioritizeUserTimeSetting !== "infinity"
							&& prioritizeUserTimeSetting * 60 * 1000 < (new Date() - new Date(ticket.createdAt))
						 )
				)
			))
		)
	);



	// 	************
	// 	** Return **
	// 	************
	if (showTicketFlag) {
		return (
			<React.Fragment key={ticket.id}>
				<TicketOptionsContextMenu
					ticket={ticket}
					ticketsList={ticketsList}
					timeAwaitingFlag={awaitingTimeSetting !== -1 && timeAwaitingNumber >= awaitingTimeSetting && !secondMetrics.includes(timeAwaitingMetric)}
					awaitingTimeSetting={awaitingTimeSetting}
					transferTicketModalOpen={handleOpenTransferModal}
					editTicketModalOpen={handleOpenEditTicketModal}
					menuOpen={ticketOptionsContextMenuOpen}
					handleClose={handleCloseTicketOptionsMenu}
					anchorEl={anchorEl}
					setTabOpen={setTabOpen}
					setPropertiesModalOpen={setPropertiesModalOpen}
				/>

				<TicketPropertiesModal
					open={propertiesModalOpen}
					onClose={() => setPropertiesModalOpen(false)}
					ticket={ticket}
				/>

				{ticket.status === "open" && (
					<TransferTicketModal
						modalOpen={transferTicketModalOpen}
						onClose={handleCloseTransferTicketModal}
						ticketid={ticket.id}
						ticketType={ticket.type}
						ticketWhatsappId={ticket.whatsappId}
						contactId={ticket.contactId}
					/>
				)}

				{["open", "pending"].includes(ticket.status) && [0, 1].includes(ticket.type) && (
					<EditTicketModal
						modalOpen={editTicketModalOpen}
						onClose={handleCloseEditTicketModal}
						ticket={ticket}
						ticketStatus={ticket.status}
					/>
				)}

				<Tooltip
					arrow
					placement="left"
					title={ticket.queue?.name || "Sem fila"}
					PopperProps={{ disablePortal: true }}
				>
					<ListItem
						dense
						button
						onClick={() => handleSelectTicket(ticket.id, ticket.type, ticket.status, ticket.contact ? ticket.contact.id : "0")}
						onContextMenu={(event) => handleContextMenu(event, ticket)}
						selected={ticketId && +ticketId === ticket.id}
						className={classes.ticket}
						style={{ 
							"-webkit-box-shadow": `inset -3px -4px ${ticket.queue?.color || "#7C7C7C"}`,
							"-moz-box-shadow": `inset -3px -4px ${ticket.queue?.color || "#7C7C7C"}`,
							boxShadow: `inset -3px -4px ${ticket.queue?.color || "#7C7C7C"}`,
						}}
					>
						<ListItemAvatar>
							{ticket.type === 0 && (<Avatar src={ticket?.contact?.name === DefaultValuesUtils.SPECIAL_NAMES.whatsapp ? WhatsappOfficialImg : ticket?.contact?.profilePicUrl} />)}
							{ticket.type === 1 && (<Avatar src={ticket?.contact?.profilePicUrl} />)}
							{ticket.type === 2 && (<Avatar src={BezinhoImg} />)}
							{ticket.type === 3 && (<Avatar src={InternalChatImg} />)}
							{ticket.type === 4 && (<Avatar src={InternalGroupImg} />)}
						</ListItemAvatar>

						<ListItemText
							disableTypography
							primary={
								<span className={classes.contactNameWrapper}>
									<span className={classes.contactNameAndDevice}>
										<img className={classes.ticketType} src={ticketTypeIcon} alt="Ticket Logo" />

										{ticket.type === 0 && ticket.contact?.temporaryChatSetting?.isEnabled && (
											<Tooltip
												title={i18n.t("tickets.notification.temporaryChat").replace("@", ticket.contact?.temporaryChatSetting?.duration)}
												placement="bottom-start"
												PopperProps={{ disablePortal: true }}
												arrow
											>
												<RestoreOutlinedIcon className={classes.simpleIcon} />
											</Tooltip>
										)}

										{ticket.status === "pending" && ticket.isReturned && (
											<Tooltip
												title={i18n.t("ticketsList.isReturned")}
												placement="bottom-start"
												arrow
												PopperProps={{ disablePortal: true }}
											>
												<ReplyOutlinedIcon className={classes.horizontalFlip} />
											</Tooltip>
										)}

										{ticket.isTransfered && (
											<Tooltip
												title={i18n.t("ticketsList.isTransfered")}
												placement="bottom-start"
												arrow
												PopperProps={{ disablePortal: true }}
											>
												<SyncAltOutlinedIcon className={classes.horizontalFlip} />
											</Tooltip>
										)}

										{ticket.hasMessageFromPhone && (
											<Tooltip
												title={i18n.t("tickets.notification.hasMessageFromPhone")}
												placement="bottom-start"
												arrow
												PopperProps={{ disablePortal: true }}
											>
												<PhoneAndroidOutlinedIcon className={classes.svgIcons} />
											</Tooltip>
										)}

										<Typography component="span" variant="body2" color="textPrimary" style={{wordBreak: "break-word"}}>
											<>
												{ticket.contact && (truncateString(ticket.contact.name, 27))}
												{!ticket.contact && (truncateString(ticket.noteName, 27))}
											</>
										</Typography>
									</span>

									{ticket.lastMessage && (
										<Typography className={classes.lastMessageTime} component="span" variant="body2" color="textSecondary">
											{isSameDay(parseISO(ticket.updatedAt), new Date())
												? (<>{format(parseISO(ticket.updatedAt), "HH:mm")}</>)
												: (<>{format(parseISO(ticket.updatedAt), "dd/MM/yyyy")}</>)}
										</Typography>
									)}
								</span>
							}
							secondary={
								<span className={classes.contactBadgeWrapper}>
									<Typography className={classes.contactLastMessage} noWrap component="span" variant="body2" color="textSecondary">
										{ticket.lastMessage
											? (<MarkdownWrapper shouldFormat={false}>{ticket.lastMessage}</MarkdownWrapper>)
											: (<br />)}

										{
											ticket.type === 0 &&
											ticket.contact &&
											ticket.contact.labels &&
											ticket.contact.labels.length !== undefined &&
											ticket.contact.labels.length > 0 &&
											(	
												<>
													<ContactTagsModal
														open={contactTagsModalOpen}
														onClose={handleCloseContactTagsModal}
														labelsList={ticket.contact.labels}
													/>

													<div
														className={ classes.customLabelBadge}
														style={{ border: `1px solid ${ticket.contact.labels[0].color}`} }													
														onClick={handleOpenContactTagsModal}
													>
														{truncateString(ticket.contact.labels[0].name)}
													</div>						
												</>
											)
										}

										<div className={classes.badgeList}>
											{!ticket.userId && !ticket.isGroup && (
												<div
													className={
														awaitingTimeSetting !== -1 && timeAwaitingNumber >= awaitingTimeSetting && !secondMetrics.includes(timeAwaitingMetric)
															? classes.customBadgeAwaitingTimeAlert
															: classes.customBadgeAwaitingTime
													}
												>
													<AccessTimeOutlinedIcon className={classes.svgIcons} /> {timeAwaiting}
												</div>
											)}

											{ticket.userId && ticket.withUserAt && (
												<div
													className={
														attendanceTimeSetting !== -1 && timeAttendanceNumber >= attendanceTimeSetting && !secondMetrics.includes(timeAttendanceMetric)
															? classes.customBadgeAwaitingTimeAlert
															: classes.customBadgeAwaitingTime
													}
												>
													<AccessTimeOutlinedIcon className={classes.svgIcons} /> {timeInAttendance}
												</div>
											)}

											{ticket.directClosedById && user.profile === "admin" && ticket.status === "closed" && (
												<div className={classes.customBadge}>
													<CloseOutlinedIcon className={classes.svgIcons}/> {ticket.directClosedByUser?.name}
												</div>
											)}

											{ticket.directClosedById && user.profile === "user" && ticket.status === "closed" && (
												<div className={classes.customBadge}>
													<CloseOutlinedIcon className={classes.svgIcons}/>
												</div>
											)}
											
											{ticket.userId && ![3, 4].includes(ticket.type) && user.profile === "admin" && (
												<div className={classes.customBadge}>
													<PersonOutlinedIcon className={classes.svgIcons}/> {ticket.user?.name}
												</div>
											)}

											{ticket.type === 3 && (
												<div className={classes.customBadge}>
													<PersonOutlinedIcon className={classes.svgIcons} />
													{ticket.user?.name} || {ticket.userInternalChat?.name}
												</div>
											)}

											{ticket.subqueueId && (
												<div className={classes.customBadge}>
													<ChatBubbleOutlineOutlinedIcon className={classes.svgIcons} /> {ticket.subqueue?.name}
												</div>
											)}

											{ticket.queueId && !ticket.subqueueId && (
												<div className={classes.customBadge}>
													<ChatBubbleOutlineOutlinedIcon className={classes.svgIcons} /> {ticket.queue?.name}
												</div>
											)}

											{ticket.whatsappId && showChipBadge && (
												<div className={classes.chipBadge}>
													<SimCardOutlinedIcon className={classes.svgIcons} /> {ticket.whatsapp?.name}  
												</div>
											)}
										</div>
									</Typography>

									<Badge
										className={classes.newBadge}
										badgeContent={
											(() => {
												// - Internal Chats when Logged In User has Created the Chat
												if (ticket.type === 3 && user.id === ticket.userId) {
													return ticket.isMarkedAsUnread ? "" : 0;
												}
												
												// - Internal Chats when Logged In User has not Created the Chat
												else if (ticket.type === 3 && user.id === ticket.userIdInternalChat) {
													return ticket.isMarkedAsUnreadInternalChat ? "" : 0;
												}

												// Whatsapp Tickets (Groups with Me Mentioned)
												else if (ticket.status === "group" && ticket.isMeMentioned) {
													return "@";
												}
												
												// - Notes, Bot Chats and Whatsapp Tickets
												else {
													if (ticket.unreadMessages !== 0 && ticket.unreadMessages) return ticket.unreadMessages;
													else return ticket.isMarkedAsUnread ? "" : 0;
												}
											})()
										}
										classes={{ badge: classes.badgeStyle, }}
									/>
								</span>
							}
						/>
					</ListItem>
				</Tooltip>

				<Divider variant="inset" component="li" />
			</React.Fragment>
		);
	}
	else {
		return (<React.Fragment key={ticket.id}></React.Fragment>);
	}
};

export default TicketListItem;
